import React from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { navigateToSubscriptionPlans } from '@/lib/auth/subscription-plans-url';
import { usePlanFeatureStore } from '@/store/planFeatureStore';
import type { PlanFeature } from '@/types/plan-access';

const plansRouter = { push: (path: string) => router.push(path as never) };

type Copy = {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  title: string;
  body: string;
};

/**
 * O que cada recurso faz por quem assina — o aviso vende o ganho, não a trava.
 * A segunda linha (comum) lembra que o gratuito continua de pé.
 */
const COPY: Record<PlanFeature, Copy> = {
  WHATSAPP: {
    icon: 'logo-whatsapp',
    title: 'Lembretes no WhatsApp',
    body: 'Com um plano, seus clientes recebem lembrete de cobrança, confirmação de pagamento e aviso de horário no WhatsApp, sem você digitar nada.',
  },
  AUTO_PAYMENT: {
    icon: 'card-outline',
    title: 'Recebimento automático',
    body: 'Com um plano, o cliente paga por Pix ou cartão no link e a cobrança dá baixa sozinha. Nada de conferir comprovante.',
  },
  PUBLIC_PROFILE: {
    icon: 'globe-outline',
    title: 'Seu perfil público',
    body: 'Com um plano, você ganha uma página com seus serviços e link de agendamento, e aparece para quem procura na busca.',
  },
  ASSISTANT: {
    icon: 'sparkles-outline',
    title: 'Assistente',
    body: 'Com um plano, é só pedir: o assistente cria serviço, ajusta a agenda e responde sobre o seu negócio.',
  },
  RECONFIGURE: {
    icon: 'refresh-outline',
    title: 'Refazer a configuração',
    body: 'Com um plano, você refaz a conversa do começo e o app se ajusta de novo ao seu negócio. Seus serviços e horários continuam editáveis por aqui.',
  },
  SUBSCRIPTIONS: {
    icon: 'repeat-outline',
    title: 'Assinaturas e recorrência',
    body: 'Com um plano, você vende mensalidade, pacote de sessões e horário fixo, com a cobrança gerada todo mês.',
  },
};

/**
 * Aviso único de "assine para liberar" do modo gratuito. Montado no layout
 * raiz; abre por `usePlanFeature().guard()` (antes de tentar) ou pela trava
 * da API (`PLAN_FEATURE_LOCKED`, no interceptor).
 */
export function PlanFeatureModal() {
  const feature = usePlanFeatureStore((s) => s.feature);
  const close = usePlanFeatureStore((s) => s.close);

  if (!feature) return null;
  const copy = COPY[feature];

  const handleVerPlanos = () => {
    close();
    void navigateToSubscriptionPlans(plansRouter);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="w-[90%] max-w-md">
        <View className="items-center pt-2">
          <View className="mb-4 h-14 w-14 items-center justify-center rounded-full bg-primary/10 dark:bg-primary">
            <Ionicons name={copy.icon} size={28} className="text-primary dark:text-white" />
          </View>
          <DialogHeader className="items-center">
            <DialogTitle className="text-center text-xl text-foreground">{copy.title}</DialogTitle>
          </DialogHeader>
          <View className="mt-3 gap-2 px-1">
            <ThemedText className="text-center text-muted-foreground">{copy.body}</ThemedText>
            <ThemedText className="text-center text-sm text-muted-foreground">
              No plano gratuito, agenda, clientes e cobranças avulsas continuam liberados.
            </ThemedText>
          </View>
          <DialogFooter className="mt-6 w-full flex-row gap-3">
            <Button title="Agora não" variant="outline" className="flex-1" onPress={close} />
            <Button title="Ver planos" className="flex-1" onPress={handleVerPlanos} />
          </DialogFooter>
        </View>
      </DialogContent>
    </Dialog>
  );
}
